import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Chip,
  CircularProgress,
  TableContainer,
  Paper,
  Divider
} from "@mui/material";
import { auth, db } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
  limit
} from "firebase/firestore";

const colors = {
  darkGreen: '#06362D',
  calPolyGreen: '#155332',
  vanilla: '#DEE2B1',
  gold: '#FFD500',
  goldenrod: '#D1A505'
};

function Roomcleaned() {
  const [propertyId, setPropertyId] = useState(null);
  const [roomsMap, setRoomsMap] = useState({});
  const [cleanedRooms, setCleanedRooms] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);

  /* ---------------- AUTH + PROPERTY ---------------- */
  useEffect(() => {
    return auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const snap = await getDoc(doc(db, "authroles", user.uid));
      if (snap.exists()) {
        setPropertyId(snap.data().propertyId);
      }
    });
  }, []);

  /* ---------------- FETCH ROOMINFOS ---------------- */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "roominfos"),
      where("propertyId", "==", propertyId)
    );

    return onSnapshot(q, (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data();
      });
      setRoomsMap(map);
    });
  }, [propertyId]);

  /* ---------------- FETCH CLEANED ROOMS ---------------- */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "cleaningrooms"),
      where("status", "==", "Cleaned"),
      orderBy("createdAt", "desc"),
      limit(500)
    );

    return onSnapshot(q, (snap) => {
      setCleanedRooms(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }))
      );
      setLoading(false);
    });
  }, [propertyId]);

  /* ---------------- FILTER ---------------- */
  const filteredRooms = cleanedRooms
    .filter((r) => roomsMap[r.roomid])
    .filter((r) =>
      r.roomno?.toLowerCase().includes(searchText.toLowerCase())
    );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
      <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
        
        <Typography variant="h5" fontWeight={600} gutterBottom sx={{ color: colors.vanilla, mb: 3 }}>
          ✅ Cleaned Rooms
        </Typography>

        <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
          <CardContent sx={{ p: 3 }}>
            <div className="container-fluid p-0 mb-3">
              <div className="row">
                <div className="col-sm-4">
                  <TextField
                    size="small"
                    placeholder="Search Room No"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    fullWidth
                    InputLabelProps={{ style: { color: colors.vanilla } }}
                    InputProps={{ style: { color: colors.vanilla } }}
                    sx={{ 
                      "& .MuiOutlinedInput-root": { 
                        borderRadius: "8px",
                        backgroundColor: colors.darkGreen,
                        "& fieldset": { borderColor: colors.goldenrod },
                        "&:hover fieldset": { borderColor: colors.gold },
                        "&.Mui-focused fieldset": { borderColor: colors.gold }
                      } 
                    }}
                  />
                </div>
              </div>
            </div>

            <Divider sx={{ mb: 3, borderColor: colors.goldenrod, opacity: 0.4 }} />

            {loading ? (
              <Box textAlign="center" py={4}>
                <CircularProgress sx={{ color: colors.gold }} />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: colors.darkGreen }}>
                    <TableRow>
                      <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}><b>#</b></TableCell>
                      <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}><b>Room No</b></TableCell>
                      <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}><b>Room Type</b></TableCell>
                      <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}><b>Cleaned By</b></TableCell>
                      <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}><b>Cleaned Time</b></TableCell>
                      <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}><b>Status</b></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredRooms.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                          No cleaned rooms found
                        </TableCell>
                      </TableRow>
                    )}

                    {filteredRooms.map((room, index) => {
                      const roomInfo = roomsMap[room.roomid];

                      return (
                        <TableRow key={room.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                          <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{index + 1}</TableCell>

                          <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                            <Typography fontWeight={600} sx={{ color: colors.gold, fontSize: '0.85rem' }}>
                              {room.roomno}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                            {roomInfo?.roomTypeName || "-"}
                          </TableCell>

                          <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                            {room.cleanedby || "-"}
                          </TableCell>

                          <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                            {room.cleanedtime || "-"}
                          </TableCell>

                          <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                            <Chip
                              label="Cleaned"
                              color="success"
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Roomcleaned;